import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  ReactEditor,
  useSlateStatic,
  useReadOnly,
  useSelected,
} from 'slate-react';
import { Transforms } from 'slate';
import { icons } from '@ublend-npm/aulaui-next';
import copy from 'copy-to-clipboard';
import { useAulaEditorContext } from '../../EditorContext';
import {
  FailedDescription,
  Failed,
  FileContainer,
  FileLink,
  FileLinkDetails,
  FileName,
  BlockButton,
  FailedIcon,
  FileEmbedContainer,
} from './File.styled';
import { MimeTypeIcon } from '../MimeTypeIcon/MimeTypeIcon';
import { Loading } from '../Loading/Loading';
import { FileImage } from './FileImage';
import { FileEntity } from '../../Entities';
import { BlockToolbar } from '../BlockToolbar/BlockToolbar';
import { FileUploadInput } from '../FileUploadInput/FileUploadInput';
import { FileVideo, isSupportedVideoType } from './FileVideo';
import { removeVoidElement } from '../../lib/commands';
import { FileAudio } from './FileAudio';
import { AltTextDrawer } from '../AltTextDrawer/AltTextDrawer';
import { InputWithActions } from '../InputWithActions/InputWithActions';
import { useAnalytics } from '../../lib/useAnalytics';
import { AulaEditorContext, BlockElement, EditorConfig } from '../../types';
import { PDFObjectContainer } from './PDFObjectContainer';
import {
  extractFileExtension,
  fileWithoutExtension,
  getFilenameErrorMessage,
} from '../../utils/files';

export type FileProps = {
  element: BlockElement;
};

type FileInfo = {
  s3Key?: string;
  downloadUrl?: string;
  fileUrl: string;
  isImage: boolean;
  isSupportedVideo: boolean;
  isAudio: boolean;
  isPdf: boolean;
  name?: string;
  mimeType?: string;
  entityId: string;
  entity: FileEntity;
};

const getS3Key = (entity: FileEntity) => {
  if (entity.data != null && entity.data.fileUrl != null) {
    /* In the past we used the url from AWS S3 inside the content to point to the address of the editor block.
    E.g (1): https://s3.REGION.amazonaws.com/INSTITUTION-parse-bucket/FILE_KEY_IN_BUCKET
    E.g (2): https://INSTITUTION-parse-bucket.REGION.amazonaws.com/FILE_KEY_IN_BUCKET
    Later we added a proxy to serve these files via e.g. https://files.INSTITUTION.aula.education/FILE_KEY_IN_BUCKET
    but decided to not change the already created content.

    This function takes the file url that is provided inside the editor block, splits it with forward-slash, and returns
    the last element. This will correctly extract the S3 key for the three formats examplified above. */
    return entity.data.fileUrl.split('/').pop();
  }
  return undefined;
};

const getDownloadUrl = (
  apiUrl: string,
  s3Key: string,
  fileName: string,
): string => {
  const objectName = encodeURIComponent(s3Key);
  const safeFileName = encodeURIComponent(fileName);

  return `${apiUrl}/file/download/?objectName=${objectName}&name=${safeFileName}`;
};

// Given a FileEntity, extracts file information used throughout the file component,
// such as resource URLs, mime type, etc
const getFileInfo = (
  entityId: string,
  entity: FileEntity,
  config: EditorConfig,
  title?: string,
): FileInfo => {
  const s3Key = getS3Key(entity);
  const { data } = entity;

  // If we have an S3 key, build a download url for this file:
  const downloadUrl =
    s3Key != null
      ? getDownloadUrl(config.apiUrl, s3Key, title || entity.file.name)
      : undefined;

  // Workaround to treat svg as a regular file instead of as an image.
  // Once this is fixed, the line below should be the only logic needed.
  let isImage = !!(
    s3Key !== null &&
    data?.type &&
    data.type.startsWith('image')
  );
  if (isImage && data?.type && data.type.includes('image/svg+xml')) {
    isImage = false;
  }

  return {
    entityId,
    entity,
    s3Key,
    downloadUrl: data?.type === false ? data.fileUrl : downloadUrl,
    name: data?.name,
    fileUrl:
      // data.type === false condition occurs only with legacy documents where
      // the file block was created by pasting URL that points to a file this
      // condition can be ignored when legacy document conversion support ends.
      data?.type === false ? data.fileUrl : `${config.filesUrl}/${s3Key}`,
    mimeType: data?.type || undefined,

    // Any mime-type starting with image/ is treated as an image
    isImage,

    // Any mime-type known by Plyr is treated as video, otherwise we handle
    // it as a regular file:
    isSupportedVideo: !!(
      s3Key !== null &&
      data?.type &&
      isSupportedVideoType(data.type)
    ),
    isAudio: !!(s3Key !== null && data?.type && data.type.startsWith('audio')),
    isPdf: s3Key !== null && data?.type === 'application/pdf',
  };
};

// Given a FileInfo object, returns the correct content component (e.g image, video, regular file)
const renderFileTypeContent = ({
  isCollapsed,
  isSelected,
  fileInfo,
  altText,
  title,
}: {
  context: AulaEditorContext;
  isCollapsed: boolean;
  isSelected: boolean;
  fileInfo: FileInfo;
  altText?: string;
  title?: string;
}) => {
  const {
    isImage,
    isSupportedVideo,
    isAudio,
    isPdf,
    name,
    fileUrl,
    mimeType,
    s3Key,
    downloadUrl,
    entity,
    entityId,
  } = fileInfo;

  if (isImage) {
    return (
      <FileImage
        entityId={entityId}
        name={title || name || ''}
        alt={altText}
        src={fileUrl}
        isCollapsed={isCollapsed}
      />
    );
  }

  // Video blocks can be collapsed to a regular file block:
  if (isSupportedVideo && !isCollapsed) {
    return <FileVideo type={mimeType} src={fileUrl} />;
  }

  if (isAudio) {
    return (
      <FileAudio
        type={mimeType}
        src={fileUrl}
        name={title || name}
        entityId={entityId}
      />
    );
  }

  const includePdfEmbed = isPdf && fileUrl && !isCollapsed;

  return (
    <FileLink
      isSelected={isSelected}
      rel="noreferrer"
      data-testid="file-link"
      as={s3Key != null ? 'a' : 'div'}
      href={downloadUrl}
      target="_blank"
    >
      <FileLinkDetails>
        <MimeTypeIcon type={entity.file.type} />
        <FileName>{title || entity.file.name}</FileName>
        {s3Key == null && (
          <Loading progress={entity?.transient?.progress || 0} />
        )}
      </FileLinkDetails>

      {includePdfEmbed ? (
        <FileEmbedContainer>
          <PDFObjectContainer url={fileUrl} />
        </FileEmbedContainer>
      ) : null}
    </FileLink>
  );
};

export const FileBlock = (props: FileProps) => {
  const { element } = props;
  const context = useAulaEditorContext();
  const { entities, config } = context;
  const isSelected = useSelected();
  const [isHovering, setHovering] = useState(false);
  const isReadOnly = useReadOnly();
  const editor = useSlateStatic();
  const [isEditingAltText, setIsEditingAltText] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleInputValue, setTitleInputValue] = useState('');
  const [titleError, setTitleError] = useState('');
  const track = useAnalytics();

  const at = ReactEditor.findPath(editor, element);

  const inputRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  const { entity: entityId } = element;
  const entity = entities[entityId];

  const resetComponent = () => {
    setTitleError('');
    setIsEditingTitle(false);
  };

  const onRetry = useCallback(() => {
    const { current } = inputRef;
    if (current == null) {
      throw new Error('inputRef must exist');
    }

    current.click();
  }, []);

  useEffect(() => {
    const titleInput = titleRef?.current;
    if (isEditingTitle && titleInput) {
      setTimeout(() => titleInput.focus(), 0);
    }
  }, [isEditingTitle, editor]);

  if (entity?.type !== 'file') return null;

  const fileInfo = getFileInfo(entityId, entity, config, element.title);
  const { fileUrl, downloadUrl, isImage, isSupportedVideo, isPdf } = fileInfo;
  const fileExtension = fileInfo?.name
    ? extractFileExtension(fileInfo.name)
    : null;

  // Currently only video and image files are collapsible
  const isCollapsibleFileType = isPdf || isSupportedVideo || isImage;
  const isCollapsible = isCollapsibleFileType && !isReadOnly;
  const isCollapsedByDefault = !isCollapsibleFileType;

  const isCollapsed =
    typeof element.collapsed === 'undefined'
      ? isCollapsedByDefault
      : element.collapsed;

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      resetComponent();
      Transforms.select(editor, at);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      const fileTitle = titleInputValue.trim();
      const errorMessage = getFilenameErrorMessage(fileTitle);
      const titleWithExtension = `${fileTitle}${fileExtension}`;

      if (errorMessage) {
        setTitleError(errorMessage);
        return;
      }

      Transforms.setNodes(
        editor,
        {
          title: titleWithExtension,
        },
        {
          at,
        },
      );

      resetComponent();
      Transforms.select(editor, at);
      ReactEditor.focus(editor);
    }
  };

  if (entity.failed) {
    return (
      <Failed
        isSelected={isSelected}
        aria-current={isSelected}
        data-testid="failed-upload"
      >
        <FailedIcon>
          <icons.ErrorIcon />
        </FailedIcon>
        <FailedDescription>
          {isImage ? 'Image upload' : 'Upload'} failed due to a network error.
          {!isReadOnly && (
            <>
              <br />
              <BlockButton onClick={onRetry}>Retry upload</BlockButton>
            </>
          )}
        </FailedDescription>
        <FileUploadInput
          onChange={() => {
            Transforms.removeNodes(editor, { at });
            Transforms.insertNodes(
              editor,
              [
                {
                  children: [
                    {
                      text: '',
                    },
                  ],
                },
              ],
              { at },
            );
          }}
          context={context}
          element={element}
          ref={inputRef}
        />
      </Failed>
    );
  }

  // Workaround to treat svg as a regular file instead of as an image.
  // Since the file will be shown a plain text, the only toolbar option that makes
  // sense is the "Download" one as it's the only way the user can see the image.
  const isNotSvg = fileInfo.mimeType !== 'image/svg+xml';
  const toolbarItems = [
    {
      title: 'Delete',
      icon: icons.Delete,
      onAction: () => removeVoidElement(editor, element),
      isVisible: !isReadOnly,
    },
    {
      title: 'Download',
      icon: icons.CloudDownloadIcon,
      onAction: () => window.open(downloadUrl),
    },

    {
      title: 'Copy link',
      icon: icons.CopyLink,
      onAction: () => copy(fileUrl),
      isVisible: isNotSvg,
    },
    {
      title: 'Rename file',
      icon: icons.EditIcon,
      isVisible: !isReadOnly,
      onAction: () => {
        setTitleInputValue(
          element.title
            ? fileWithoutExtension(element.title)
            : fileWithoutExtension(fileInfo.name || ''),
        );

        Transforms.deselect(editor);
        Transforms.select(editor, at);
        ReactEditor.focus(editor);

        setTimeout(() => {
          setIsEditingTitle(true);
        }, 100);
      },
    },
    {
      title: 'Edit alt text',
      icon: icons.AltText,
      isVisible: isImage && !isReadOnly,
      onAction: () => {
        setIsEditingAltText(true);
      },
    },
    {
      title: isCollapsed ? 'Expand' : 'Collapse',
      icon: isCollapsed ? icons.Fullscreen : icons.FullscreenExit,
      isVisible: isCollapsible,
      onAction: () => {
        track(isCollapsed ? 'EXPAND_FILE_BLOCK' : 'COLLAPSE_FILE_BLOCK');

        Transforms.setNodes(
          editor,
          {
            collapsed: !isCollapsed,
          },
          {
            at,
          },
        );
      },
    },
    {
      title: 'Open in new tab',
      icon: icons.OpenInNew,
      // Video files cannot be opened in a new tab:
      isVisible: !fileInfo.isSupportedVideo && isNotSvg,
      onAction: () => window.open(fileUrl),
    },
  ];

  return (
    <>
      <AltTextDrawer
        open={isEditingAltText}
        src={fileInfo.fileUrl}
        altText={element.altText}
        onChange={(altText) => {
          Transforms.setNodes(
            editor,
            {
              altText,
            },
            {
              at,
            },
          );
          setIsEditingAltText(false);
        }}
        onClose={() => {
          setTimeout(() => {
            ReactEditor.focus(editor);
          }, 0);
          setIsEditingAltText(false);
        }}
      />
      <FileContainer
        data-testid="file"
        maxContentWidth={isImage && !isCollapsed}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        aria-current={isSelected}
      >
        {renderFileTypeContent({
          context,
          isCollapsed,
          isSelected,
          fileInfo,
          altText: element.altText,
          title: element.title,
        })}

        <BlockToolbar
          isVisible={!isEditingTitle && (isSelected || isHovering)}
          items={toolbarItems}
        />

        <InputWithActions
          dataTestId="rename-file-input"
          inputRef={titleRef}
          inputValue={titleInputValue}
          isVisible={isEditingTitle}
          onDismiss={resetComponent}
          onChange={(e: any) => {
            if (titleError) setTitleError('');
            setTitleInputValue(e.target.value);
          }}
          onInputKeyDown={onInputKeyDown}
          label="File name"
          showError={!!titleError}
          errorText={titleError}
          endAdornment={fileExtension}
        />
      </FileContainer>
    </>
  );
};
